/* oxlint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers  */
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { GspClaimReason } from '~/utils/constants';
import {
  customerInfoSchema,
  gspOrderSchema,
  mediaAssetSchema,
  shippingClaimSchema,
} from '~/utils/schemas';
import {
  completeShippingProtectionClaim,
  reset,
  setOrder,
} from './sharedActions';

export const shippingProtectionClaimStateSchema = z
  .object({
    status: z.enum(['new', 'completed']),
    orderIdFromPlatform: z.string(),
    reason: z.nullable(z.enum(GspClaimReason)),
    itemIds: z.array(gspOrderSchema.shape.lineItems.element.shape.id),
    meta: z.nullable(
      z.object({
        noteFromCustomer: shippingClaimSchema.shape.noteFromCustomer.optional(),
        assets: z.array(mediaAssetSchema).optional(),
      }),
    ),
    customerInfo: z.nullable(
      customerInfoSchema.omit({ email: true }).required({ address: true }),
    ),
  })
  .nullable();

type ShippingProtectionClaim = z.infer<
  typeof shippingProtectionClaimStateSchema
>;
function assertInitialized(
  state: Draft<ShippingProtectionClaim>,
): asserts state is Draft<NonNullable<ShippingProtectionClaim>> {
  if (state === null) {
    throw new Error('Crew claim state is not initialized');
  }
}

export const shippingProtectionClaimSlice = createSlice({
  name: 'shippingProtectionClaim',
  initialState: null as ShippingProtectionClaim,
  reducers: {
    startClaim: (
      state,
      action: PayloadAction<
        Pick<NonNullable<ShippingProtectionClaim>, 'orderIdFromPlatform'>
      >,
    ) => ({
      status: 'new',
      orderIdFromPlatform: action.payload.orderIdFromPlatform,
      reason: null,
      itemIds: [],
      meta: null,
      customerInfo: null,
    }),
    setReason: (
      state,
      action: PayloadAction<NonNullable<ShippingProtectionClaim>['reason']>,
    ) => {
      assertInitialized(state);
      state.reason = action.payload;
    },
    setItemIds: (
      state,
      action: PayloadAction<NonNullable<ShippingProtectionClaim>['itemIds']>,
    ) => {
      assertInitialized(state);
      state.itemIds = action.payload;
    },
    setMeta: (
      state,
      action: PayloadAction<NonNullable<ShippingProtectionClaim>['meta']>,
    ) => {
      assertInitialized(state);
      state.meta = action.payload;
    },
    setCustomerInfo: (
      state,
      action: PayloadAction<
        NonNullable<ShippingProtectionClaim>['customerInfo']
      >,
    ) => {
      assertInitialized(state);
      state.customerInfo = action.payload;
    },
    completeClaim: (state) => {
      assertInitialized(state);
      state.status = 'completed';
    },
    clear: () => null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(setOrder, (state, action) =>
        action.payload.idFromPlatform === state?.orderIdFromPlatform
          ? state
          : null,
      )
      .addCase(completeShippingProtectionClaim, (state) => {
        assertInitialized(state);
        state.status = 'completed';
      })
      .addCase(reset, () => null);
  },
});
